import React, { memo } from "react";
import { TouchableOpacity } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

// ─── Variant types ────────────────────────────────────────────────────────────

/**
 * Visual variants for the Pill component.
 *
 * - `contact` — Figma node 12027-38698: filled gray (`stroke-50`) background
 *   with blue (`blue-50`) text. Used for the "Contact Support" header CTA.
 * - `outline` — white background with a `stroke-50` border and muted text.
 *   Useful for secondary/status pills where a subtler treatment is needed.
 * - `tag` — blue filled pill (`blue-5` bg, `blue-10` border) with dark text.
 *   Matches the Figma compliance category/industry chip style.
 */
export type PillVariant = "contact" | "outline" | "tag";

export interface PillProps {
  /** Label text (translation key). Omit when using `plainLabel`. */
  label?: TranslationKeys;
  /** Plain string label — use for dynamic strings that are not translation keys. */
  plainLabel?: string;
  /** Visual style of the pill. Defaults to `"contact"`. */
  variant?: PillVariant;
  /** Called when the pill is pressed. */
  onPress?: () => void;
  /** Forwarded to the root element for testing. */
  testID?: string;
  /** Screen-reader label. Defaults to `label`. */
  accessibilityLabel?: TranslationKeys;
  /** Additional class names for custom styling. */
  className?: string;
  /** Optional icon to display to the left of the label. */
  icon?: React.ReactNode;
}

// ─── Variant styles ──────────────────────────────────────────────────────────

const PILL_STYLES: Record<PillVariant, { container: string; text: string }> = {
  // Figma: bg Stroke/50 (#E4E7ED), text Blue/50 (#1F6CFF), fully rounded
  contact: {
    container: "bg-stroke-50 rounded-full px-3 py-2",
    text: "text-blue-50 text-sm",
  },
  // Subtle outlined variant — white bg, stroke border, muted text
  outline: {
    container: "border border-stroke-50 rounded-full px-4 py-2 bg-white",
    text: "text-dark-text-20 text-xs",
  },
  // Figma Blue/05 bg + Blue/10 border — used for category/industry chips
  tag: {
    container: "bg-blue-5 border border-blue-10 rounded-full px-4 py-2",
    text: "text-dark-text-50 text-base",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Pill / badge button for compact CTAs and status labels.
 *
 * Extracted from Figma node 12027-38698 ("contact" pill — Contact Support
 * header button). The `outline` variant matches the previous ScreenHeader
 * bordered treatment and is kept for backwards-compatible usage.
 *
 * @example
 * <Pill label="common.contactSupport" variant="contact" onPress={handler} />
 */
export const Pill = memo(function Pill({
  label,
  plainLabel,
  variant = "contact",
  onPress,
  testID,
  accessibilityLabel,
  className,
  icon,
}: PillProps) {
  const { t } = useI18n();
  const styles = PILL_STYLES[variant];
  const displayText = plainLabel ?? (label ? t(label) : "");
  const a11yLabel = accessibilityLabel ? t(accessibilityLabel) : displayText;

  return (
    <TouchableOpacity
      onPress={onPress}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      disabled={!onPress}
      className={cn("flex-row gap-2 items-center", styles.container, className)}
    >
      {icon}
      <AppText weight="medium" className={styles.text}>
        {displayText}
      </AppText>
    </TouchableOpacity>
  );
});
